/**
 * DataList header component.
 *
 * Renders the title area with an optional icon, custom children, and a
 * collapse/expand toggle when the list is configured as collapsible.
 *
 * Title resolution order: props.title > config.i18n.title > config.display.title.
 * Icon resolution order:  props.icon  > config.display.titleIcon.
 */

import { icons, type LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Text } from '../../../base/display/Text/Text';
import { Icon } from '../../../base/display/Icon/Icon';
import { Button } from '../../../base/actions/Button/Button';
import { useDataListContext } from '../context/DataListContext';
import type { DataListHeaderProps } from '../types';

// ---------------------------------------------------------------------------
// Icon Resolution Helper
// ---------------------------------------------------------------------------

/**
 * Converts a kebab-case icon name (e.g. "chevron-down") to PascalCase
 * (e.g. "ChevronDown") and looks it up in the Lucide icons registry.
 *
 * Returns `undefined` when the name cannot be resolved.
 */
function getIconComponent(name: string): LucideIcon | undefined {
  // Convert kebab-case to PascalCase: "list-filter" -> "ListFilter"
  const pascalCase = name
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');

  return (icons as Record<string, LucideIcon>)[pascalCase];
}

// ---------------------------------------------------------------------------
// DataListHeader
// ---------------------------------------------------------------------------

export function DataListHeader({
  title,
  icon,
  children,
  className,
}: Readonly<DataListHeaderProps>) {
  const { config, list } = useDataListContext();

  // Resolve title: props > i18n > config display
  const resolvedTitle =
    title ?? config.i18n?.title ?? config.display?.title;

  // Resolve icon: props > config display
  const resolvedIcon = icon ?? config.display?.titleIcon;

  const isCollapsible = config.display?.collapsible ?? false;

  // Resolve the Lucide icon component from the string name
  const IconComponent = resolvedIcon
    ? getIconComponent(resolvedIcon)
    : undefined;

  const combinedClasses = [
    'flex items-center justify-between',
    'bg-secondary-50 border-b border-secondary-200 px-4 py-3 rounded-t-lg',
    config.cssOverrides?.header,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClasses}>
      <div className="flex items-center gap-2">
        {IconComponent && <Icon icon={IconComponent} />}
        {resolvedTitle && (
          <Text as="h2" size="lg" weight="semibold">
            {resolvedTitle}
          </Text>
        )}
      </div>
      <div className="flex items-center gap-2">
        {children}
        {isCollapsible && (
          <Button
            variant="ghost"
            size="sm"
            onClick={list.toggleCollapsed}
            aria-expanded={!list.isCollapsed}
            aria-label={list.isCollapsed ? 'Expand' : 'Collapse'}
          >
            <Icon
              icon={list.isCollapsed ? ChevronDown : ChevronUp}
              size="sm"
            />
          </Button>
        )}
      </div>
    </div>
  );
}

DataListHeader.displayName = 'DataListHeader';
